// Stage 4 — shared "From Vault" provenance badge.
// Used beside a document's header (NOT on every line item) in both Upload &
// Extract and Transactions. Never creates a broken link: only renders as a
// clickable link once the Vault original's existence is confirmed; otherwise
// renders a plain, non-clickable badge.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderArchive } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Module-level de-dupe: several badges can render on one page (Transactions
// has one per document group) — fetch /vault/list exactly once per page load
// instead of once per badge instance.
let _vaultListPromise = null;
function fetchVaultList() {
  if (!_vaultListPromise) {
    _vaultListPromise = axios.get(`${API}/vault/list`, { withCredentials: true })
      .then((r) => r.data.files || [])
      .catch(() => []);
  }
  return _vaultListPromise;
}

export function VaultProvenanceBadge({ sourceVaultFileId }) {
  const navigate = useNavigate();
  const [exists, setExists] = useState(null); // null = still resolving

  useEffect(() => {
    if (!sourceVaultFileId) return;
    let cancelled = false;
    fetchVaultList().then((files) => {
      if (!cancelled) setExists(files.some((f) => f.id === sourceVaultFileId));
    });
    return () => { cancelled = true; };
  }, [sourceVaultFileId]);

  if (!sourceVaultFileId || exists === null) return null;

  if (exists) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(`/app/vault?focus_file=${sourceVaultFileId}`); }}
        title="View the original file in the On-The-Go Vault"
        data-testid={`from-vault-badge-${sourceVaultFileId}`}
        className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 transition-colors"
      >
        <FolderArchive className="w-3 h-3" /> From Vault
      </button>
    );
  }

  return (
    <span
      title="The original Vault file was deleted. This processed document and its transactions are unaffected."
      data-testid={`from-vault-badge-deleted-${sourceVaultFileId}`}
      className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0"
    >
      <FolderArchive className="w-3 h-3" /> From Vault (original deleted)
    </span>
  );
}
